import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  CheckCircle2, XCircle, MinusCircle, CircleSlash, RefreshCw, Loader2, ChevronDown, ChevronUp,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";

const LOOK = {
  pass: { Icon: CheckCircle2, cls: "text-emerald-500" },
  fail: { Icon: XCircle, cls: "text-red-500" },
  off: { Icon: CircleSlash, cls: "text-muted-foreground" },
  na: { Icon: MinusCircle, cls: "text-amber-500" },
};

/**
 * WO9 Section B — the go-live checklist. A report, never a switch: nothing on this card turns a
 * feature on. Marking a row N/A (staff only, reason required) is the only thing a person can change.
 */
export function ReadinessCard({ siteId }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");
  const [open, setOpen] = useState(true);
  const [reason, setReason] = useState({});

  const load = async (refresh = false) => {
    setBusy(refresh ? "refresh" : "load");
    try {
      const { data } = await api.get(`/sites/${siteId}/readiness`, { params: refresh ? { refresh: true } : {} });
      setData(data);
      (data.na_cleared || []).forEach((c) =>
        toast.info(`"${c.check_id}" is no longer marked N/A — ${c.why}.`));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not run the readiness check");
    } finally { setBusy(""); }
  };
  useEffect(() => { load(); }, [siteId]); // eslint-disable-line react-hooks/exhaustive-deps

  const markNa = async (id) => {
    const why = (reason[id] || "").trim();
    if (why.length < 4) { toast.error("Say why this row doesn't apply — it goes on record."); return; }
    setBusy("na" + id);
    try {
      const { data } = await api.post(`/sites/${siteId}/readiness/${id}/na`, { reason: why });
      setData(data);
      setReason((r) => ({ ...r, [id]: undefined }));
      toast.success("Marked N/A. It expires in 90 days.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not mark that row");
    } finally { setBusy(""); }
  };

  const clearNa = async (id) => {
    setBusy("na" + id);
    try {
      const { data } = await api.delete(`/sites/${siteId}/readiness/${id}/na`);
      setData(data);
      toast.success("N/A removed — the row is checked again.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not clear that row");
    } finally { setBusy(""); }
  };

  if (!data) {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-border bg-card px-5 py-4 text-sm text-muted-foreground"
        data-testid="readiness-loading">
        <Loader2 className="h-4 w-4 animate-spin" /> Checking go-live readiness…
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-border bg-card p-5" data-testid="readiness-card">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <p className="font-display text-lg font-semibold tracking-tight">Go-live readiness</p>
          {data.ready ? (
            <Badge className="border-emerald-500/30 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
              data-testid="readiness-badge">Ready</Badge>
          ) : (
            <Badge className="border-red-500/30 bg-red-500/10 text-red-600 dark:text-red-400"
              data-testid="readiness-badge">Not ready · {data.failing.length} to fix</Badge>
          )}
        </div>
        <div className="flex items-center gap-1.5">
          <Button size="sm" variant="outline" disabled={!!busy} onClick={() => load(true)}
            data-testid="readiness-recheck">
            {busy === "refresh" ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
              : <RefreshCw className="mr-1.5 h-3.5 w-3.5" />} Re-check now
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setOpen((o) => !o)} data-testid="readiness-toggle">
            {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </Button>
        </div>
      </div>
      <p className="mt-1 text-xs text-muted-foreground" data-testid="readiness-note">{data.note}</p>

      {open && (
        <div className="mt-4 space-y-2" data-testid="readiness-rows">
          {data.rows.map((r) => {
            const { Icon, cls } = LOOK[r.status] || LOOK.off;
            return (
              <div key={r.id} className="rounded-md border border-border bg-background px-3 py-2"
                data-testid={`readiness-row-${r.id}`}>
                <div className="flex items-start gap-2">
                  <Icon className={`mt-0.5 h-4 w-4 shrink-0 ${cls}`} data-testid={`readiness-status-${r.id}`} />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium">{r.label}</p>
                    <p className="text-xs text-muted-foreground" data-testid={`readiness-evidence-${r.id}`}>
                      {r.evidence}
                    </p>
                    {r.status === "fail" && !!r.fix && (
                      <p className="mt-1 text-xs text-foreground/80" data-testid={`readiness-fix-${r.id}`}>
                        → {r.fix}
                      </p>
                    )}
                    {r.status === "na" && (
                      <p className="mt-1 text-xs text-amber-600 dark:text-amber-400" data-testid={`readiness-na-${r.id}`}>
                        {r.na?.days_left} day(s) left on this N/A
                      </p>
                    )}
                    {data.can_override && r.status === "fail" && (
                      <div className="mt-2 flex items-center gap-1.5">
                        <Input className="h-8 max-w-xs text-xs" placeholder="Why doesn't this apply?"
                          data-testid={`readiness-na-reason-${r.id}`}
                          value={reason[r.id] ?? ""}
                          onChange={(e) => setReason((x) => ({ ...x, [r.id]: e.target.value }))} />
                        <Button size="sm" variant="outline" disabled={busy === "na" + r.id}
                          data-testid={`readiness-na-btn-${r.id}`} onClick={() => markNa(r.id)}>
                          Mark N/A
                        </Button>
                      </div>
                    )}
                    {data.can_override && r.status === "na" && (
                      <Button size="sm" variant="ghost" className="mt-1" disabled={busy === "na" + r.id}
                        data-testid={`readiness-na-clear-${r.id}`} onClick={() => clearNa(r.id)}>
                        Remove N/A
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
